import cn from 'classnames'
import Image from 'next/image'
import {FC, useState} from 'react'

import {OfficeCountry, useOfficeLocationsContent} from '@/content/officeLocations'
import {useTranslation} from '@/i18n'
import {MIN_TARGET_SIZE, WORLD_MAP_DESKTOP_HEIGHT, WORLD_MAP_DESKTOP_WIDTH} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {alpha2ToName} from '@/utils/countries/translations'
import {useLanguage} from '@/utils/i18n/language'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Text, TextVariant} from '../content/Text'
import {OfficeLocationCard} from './OfficeLocationCard'
import {OfficeLocationsCarousel} from './OfficeLocationsCarousel'

export const OfficeLocations: FC<React.PropsWithChildren<unknown>> = () => {
  const {t} = useTranslation('page')
  const language = useLanguage()
  const {title, offices, pointAriaLabel} = useOfficeLocationsContent()
  const [activeOffice, setActiveOffice] = useState(1)

  return (
    <>
      <Text variant={TextVariant.h2} align="center">
        {t(title)}
      </Text>
      <div className="content">
        <div className="desktop">
          <div className="map">
            <Image
              src="/images/world-map-points.svg"
              alt="World map"
              style={{objectFit: 'contain'}}
              fill
              sizes="100vw"
            />
          </div>
          {Object.keys(offices).map((country, i) => {
            const isActive = i === activeOffice
            const className = country.toLowerCase()
            const countryName = alpha2ToName(country, language)

            return (
              <div key={country} className={cn('office', isActive && 'active', className)}>
                <button
                  onClick={() => !isActive && setActiveOffice(i)}
                  type="button"
                  className="point"
                  aria-label={t(pointAriaLabel, {country: countryName})}
                  aria-pressed={isActive}
                />
                {isActive && (
                  <div className="card">
                    <OfficeLocationCard
                      key={country}
                      country={country as OfficeCountry}
                      setActiveOffice={setActiveOffice}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <div className="mobile">
          <OfficeLocationsCarousel />
        </div>
      </div>

      <style jsx>{`
        .content {
          margin-top: ${theme.spacing[8]};
        }
        .office {
          position: absolute;
        }
        .desktop {
          position: relative;
          width: ${WORLD_MAP_DESKTOP_WIDTH};
          margin: 0 auto;
        }
        .map {
          position: relative;
          width: 100%;
          height: ${WORLD_MAP_DESKTOP_HEIGHT};
        }
        .point {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          width: ${MIN_TARGET_SIZE}px;
          height: ${MIN_TARGET_SIZE}px;
          margin: -${theme.spacing[0.5]};
        }
        .point::before {
          content: '';
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background-color: ${theme.palette.primary.main};
        }
        .active .point {
          border-radius: 50%;
          animation: pulse 1500ms infinite;
        }
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 ${theme.palette.info.main};
          }
          70% {
            box-shadow: 0 0 0 12px rgba(255, 0, 0, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
          }
        }
        .card {
          position: absolute;
          z-index: 1;
        }
        .us {
          top: 130px;
          left: 237px;
        }
        .uk {
          top: 92px;
          left: 444px;
        }
        .sk {
          top: 104px;
          left: 498px;
        }
        .au {
          bottom: 65px;
          right: 75px;
        }
        .ca {
          top: 115px;
          left: 205px;
        }
        .us .card,
        .uk .card,
        .sk .card,
        .ca .card {
          top: 28px;
          left: 2px;
        }
        .au .card {
          bottom: 2px;
          right: 32px;
        }
        .mobile {
          display: none;
        }
        @media ${TABLET_SCREENSIZE} {
          .content {
            margin-top: ${theme.spacing[7]};
          }
          .desktop {
            display: none;
          }
          .mobile {
            display: block;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .content {
            margin-top: ${theme.spacing[6]};
          }
        }
      `}</style>
    </>
  )
}
